import { useForm } from "@inertiajs/react";
import { Upload } from "lucide-react";
import { useMemo, useState } from "react";

import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
interface CompanyLogoUploaderProps {
  updateUrl?: string;
  initialLogoUrl?: string | null;
  methodOverride?: "put";
  controlledFile?: File | null;
  onFileChange?: (file: File | null) => void;
  errorMessage?: string;
  processing?: boolean;
  hideSubmit?: boolean;
}

export default function CompanyLogoUploader({
  updateUrl,
  initialLogoUrl,
  methodOverride,
  controlledFile,
  onFileChange,
  errorMessage,
  processing: externalProcessing,
  hideSubmit = false,
}: CompanyLogoUploaderProps) {
  const [filePreview, setFilePreview] = useState<string | null>(null);

  const isControlled = typeof onFileChange === "function";

  const { data, setData, post, processing, errors, reset } = useForm<{
    company: { logo: File | null };
    _method?: string;
  }>({
    company: { logo: null },
    ...(methodOverride && { _method: methodOverride }),
  });

  const effectiveProcessing = isControlled
    ? (externalProcessing ?? false)
    : processing;
  const effectiveFile = isControlled ? controlledFile : data.company.logo;
  const effectiveError = isControlled ? errorMessage : errors["company.logo"];

  const logoPreview = useMemo(() => {
    return filePreview ?? initialLogoUrl ?? null;
  }, [filePreview, initialLogoUrl]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (isControlled) {
      onFileChange?.(file);
    } else {
      setData("company", { logo: file });
    }

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFilePreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isControlled || !updateUrl) return;
    post(updateUrl, {
      forceFormData: true,
      onSuccess: () => {
        reset();
        setFilePreview(null);
      },
    });
  };

  const handleCancel = () => {
    if (isControlled) {
      onFileChange?.(null);
    } else {
      setData("company", { logo: null });
    }
    setFilePreview(null);
  };

  const shouldUseForm = !hideSubmit && !isControlled && Boolean(updateUrl);

  const inputSection = (
    <div className="space-y-2">
      <Label htmlFor="logo">Seleccionar imagen</Label>
      <div className="flex items-center gap-3">
        <Input
          id="logo"
          type="file"
          accept="image/*"
          onChange={handleLogoChange}
          disabled={effectiveProcessing}
          className="flex-1"
        />
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          PNG, JPG, GIF
        </span>
      </div>
      <InputError message={effectiveError} />
    </div>
  );

  const actions =
    !hideSubmit && effectiveFile ? (
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={effectiveProcessing}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={effectiveProcessing} className="gap-2">
          <Upload className="h-4 w-4" />
          {effectiveProcessing ? "Actualizando..." : "Actualizar logo"}
        </Button>
      </div>
    ) : null;

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Logo de la empresa</h3>

      {logoPreview && (
        <div className="flex items-center gap-4">
          <div className="relative h-20 w-20 rounded-lg border bg-gray-100 overflow-hidden">
            <img
              src={logoPreview}
              alt="Logo preview"
              className="h-full w-full object-contain p-1"
            />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">
              {effectiveFile?.name ?? "Logo cargado"}
            </p>
          </div>
        </div>
      )}

      {shouldUseForm ? (
        <form onSubmit={handleSubmit} className="space-y-2">
          {inputSection}
          {actions}
        </form>
      ) : (
        <div className="space-y-2">
          {inputSection}
          {actions}
        </div>
      )}
    </div>
  );
}
